import { useState } from "react";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import SectionTitle from "@/components/base/SectionTitle";
import { cases } from "@/mocks/cases";

export default function Cases() {
  const [activeSlug, setActiveSlug] = useState<string>(cases[0].slug);
  const active = cases.find((c) => c.slug === activeSlug) ?? cases[0];

  const categories = ["すべて", ...Array.from(new Set(cases.map((c) => c.category)))];
  const [filter, setFilter] = useState<string>("すべて");
  const filtered = filter === "すべて" ? cases : cases.filter((c) => c.category === filter);

  return (
    <Layout>
      <PageHero
        eyebrow="Case Studies"
        title="事例紹介"
        description="実際の事例です。清掃会社選びの参考にされてください。"
        image="images/case.webp"
      />

      <section className="bg-background-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-10">
          <SectionTitle
            eyebrow="Cases"
            title="事例一覧"
            description="事例一覧です。詳細は下の事例詳細でご覧いただけます。"
          />

          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter(c)}
                className={`text-sm px-4 py-2 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                  filter === c
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {filtered.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => {
                  setActiveSlug(c.slug);
                  document
                    .getElementById("case-detail")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`group text-left bg-background-100 rounded-lg overflow-hidden border transition-colors cursor-pointer flex flex-col ${
                  active.slug === c.slug ? "border-primary-500" : "border-background-200 hover:border-primary-300"
                }`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-3 left-3 bg-primary-900/85 backdrop-blur px-3 py-1.5 rounded-md text-xs text-background-50">
                    {c.category}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-600 mb-3">
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{c.location}</span>
                    <span className="flex items-center gap-1"><i className="ri-building-2-line"></i>{c.scale}</span>
                    <span className="flex items-center gap-1"><i className="ri-price-tag-3-line"></i>{c.price}</span>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 leading-snug mb-3">{c.title}</h3>
                  <p className="text-sm text-foreground-700 leading-relaxed line-clamp-3">{c.problem}</p>
                  <div className="mt-4 text-sm text-primary-600 group-hover:text-primary-700 flex items-center gap-1">
                    詳細を見る <i className="ri-arrow-down-line"></i>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="case-detail" className="bg-background-100 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4 md:px-10">
          <SectionTitle eyebrow="Case Detail" title="事例詳細" description={active.title} />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12">
            <div className="md:col-span-3 rounded-lg overflow-hidden border border-background-200">
              <img src={active.image} alt={active.title} className="w-full h-96 md:h-[520px] object-cover object-top" />
            </div>
            <div className="md:col-span-2 flex flex-col">
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-background-50 rounded-md border border-background-200 p-4">
                  <div className="text-xs text-foreground-600 mb-1">エリア</div>
                  <div className="text-sm text-foreground-900 font-medium">{active.location}</div>
                </div>
                <div className="bg-background-50 rounded-md border border-background-200 p-4">
                  <div className="text-xs text-foreground-600 mb-1">物件規模</div>
                  <div className="text-sm text-foreground-900 font-medium">{active.scale}</div>
                </div>
                <div className="bg-background-50 rounded-md border border-background-200 p-4">
                  <div className="text-xs text-foreground-600 mb-1">期間</div>
                  <div className="text-sm text-foreground-900 font-medium">{active.period}</div>
                </div>
                <div className="bg-background-50 rounded-md border border-background-200 p-4">
                  <div className="text-xs text-foreground-600 mb-1">費用</div>
                  <div className="text-sm text-primary-700 font-medium">{active.price}</div>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <div className="text-xs text-accent-700 tracking-widest uppercase mb-2">課題</div>
                  <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{active.problem}</p>
                </div>
                <div>
                  <div className="text-xs text-accent-700 tracking-widest uppercase mb-2">施策</div>
                  <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{active.solution}</p>
                </div>
                <div>
                  <div className="text-xs text-accent-700 tracking-widest uppercase mb-2">結果</div>
                  <p className="text-sm md:text-base text-foreground-800 leading-relaxed">{active.result}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}